import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Crown, Key, Eye, Shield, ChevronRight } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

// ─── LUXURY HERO ───
export function LuxuryHero() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'hero');
  const c = {
    subheading: "By Invitation Only",
    heading: "Residences Beyond Compare.",
    description: "Curating the world's most exclusive penthouses and ultra-luxury estates for discerning collectors.",
    buttonText: "Request Private Viewing",
    buttonLink: "/portfolio",
    buttonText2: "Private Inquiry",
    buttonLink2: "/contact",
    videoUrl: "https://videos.pexels.com/video-files/3773486/3773486-uhd_2560_1440_30fps.mp4",
    imageUrl: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80",
    stats: [{ value: "$2B+", label: "Portfolio Value" }, { value: "50+", label: "Exclusive Estates" }, { value: "100%", label: "Privacy" }],
    ...content,
  };
  return (
    <section className="relative min-h-screen flex items-end overflow-hidden">
      <div className="absolute inset-0">
        {c.videoUrl ? (
          <video autoPlay muted loop playsInline className="w-full h-full object-cover" poster={c.imageUrl}>
            <source src={c.videoUrl} type="video/mp4" />
          </video>
        ) : (
          <img src={c.imageUrl} alt="Hero" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 pb-20 pt-40 relative z-10 w-full">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5 }}>
          <span className="text-accent text-xs font-medium uppercase tracking-[0.3em] mb-8 block">{c.subheading}</span>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light text-primary-foreground leading-[0.95] mb-8">{c.heading}</h1>
          <p className="text-primary-foreground/60 text-lg max-w-lg mb-10">{c.description}</p>
          <div className="flex items-center gap-8">
            <Link to={c.buttonLink || "/portfolio"} className="inline-flex items-center gap-3 text-accent text-sm uppercase tracking-wider font-medium hover:gap-5 transition-all">
              {c.buttonText} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={c.buttonLink2 || "/contact"} className="inline-flex items-center gap-3 text-primary-foreground/60 text-sm uppercase tracking-wider hover:text-primary-foreground transition-colors">
              {c.buttonText2 || "Private Inquiry"} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.8 }}
          className="flex gap-12 mt-20 pt-10 border-t border-primary-foreground/10">
          {(c.stats || []).map((s: any) => (
            <div key={s.label}>
              <div className="font-serif text-2xl text-primary-foreground">{s.value}</div>
              <div className="text-primary-foreground/40 text-xs uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── LUXURY EXCLUSIVE GALLERY ───
export function LuxuryExclusiveGallery() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'exclusive-gallery');
  const c = {
    heading: "The Collection",
    subheading: "Curated Selection",
    description: "Hand-selected properties for the world's elite.",
    items: [
      { title: "The Penthouse", description: "Golf Course Road · On Request", imageUrl: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80" },
      { title: "Villa Royale", description: "DLF Phase 5 · On Request", imageUrl: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80" },
      { title: "Sky Mansion", description: "Sector 54 · On Request", imageUrl: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80" },
      { title: "The Estate", description: "Chattarpur · On Request", imageUrl: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80" },
    ],
    ...content,
  };
  return (
    <section className="py-24 bg-background">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="flex justify-between items-end mb-16">
          <div>
            <span className="text-accent text-xs uppercase tracking-[0.3em] block mb-4">{c.subheading}</span>
            <h2 className="font-serif text-4xl md:text-5xl font-light">{c.heading}</h2>
          </div>
          <Link to="/portfolio" className="hidden md:inline-flex items-center gap-2 text-accent text-sm uppercase tracking-wider hover:gap-4 transition-all">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(c.items || []).map((p: any, i: number) => (
            <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
              className={`relative group cursor-pointer overflow-hidden ${i === 0 ? 'md:row-span-2 md:h-full' : 'h-72'}`}>
              <img src={p.imageUrl || '/placeholder.svg'} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                <h3 className="font-serif text-2xl text-primary-foreground mb-1">{p.title}</h3>
                <p className="text-primary-foreground/60 text-sm">{p.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── LUXURY CONCIERGE SERVICES ───
export function LuxuryConciergeServices() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'concierge-services');
  const c = {
    heading: "White Glove Service",
    subheading: "Bespoke Service",
    description: "End-to-end luxury real estate concierge.",
    imageUrl: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=700&q=80",
    items: [
      { title: "Private Viewings", description: "Exclusive property tours at your convenience." },
      { title: "Acquisition Advisory", description: "End-to-end support from search to possession." },
      { title: "Legal & Compliance", description: "Complete due diligence and documentation." },
      { title: "Interior Curation", description: "World-renowned interior design partnerships." },
    ],
    ...content,
  };
  const defaultIcons = [Eye, Key, Shield, Crown];
  return (
    <section className="py-24 bg-card">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-accent text-xs uppercase tracking-[0.3em] block mb-4">{c.subheading}</span>
            <h2 className="font-serif text-4xl md:text-5xl font-light mb-8">{c.heading}</h2>
            <div className="space-y-8">
              {(c.items || []).map((s: any, i: number) => {
                const Icon = defaultIcons[i % defaultIcons.length];
                return (
                  <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                    className="flex gap-5 group">
                    <div className="w-12 h-12 rounded-full border border-accent/30 flex items-center justify-center flex-shrink-0 group-hover:bg-accent group-hover:border-accent transition-all">
                      <Icon className="w-5 h-5 text-accent group-hover:text-accent-foreground transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg mb-1">{s.title}</h3>
                      <p className="text-muted-foreground text-sm">{s.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
          <div className="hidden lg:block">
            <img src={c.imageUrl || '/placeholder.svg'} alt="Luxury interior" className="w-full h-[500px] object-cover" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── LUXURY LIFESTYLE ───
export function LuxuryLifestyle() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'luxury-lifestyle');
  const c = {
    heading: "The Art of Living",
    subheading: "Beyond Real Estate",
    description: "Where architecture meets aspiration.",
    items: [
      { title: "Private Art Collections", description: "Curated art experiences", imageUrl: "https://images.unsplash.com/photo-1577083288073-40892c0860a4?w=500&q=80" },
      { title: "Michelin Dining", description: "World-class culinary", imageUrl: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=500&q=80" },
      { title: "Yacht & Aviation", description: "Exclusive travel", imageUrl: "https://images.unsplash.com/photo-1540946485063-a40da27545f8?w=500&q=80" },
    ],
    ...content,
  };
  return (
    <section className="py-24 bg-primary text-primary-foreground">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
          <span className="text-accent text-xs uppercase tracking-[0.3em] block mb-4">{c.subheading}</span>
          <h2 className="font-serif text-4xl md:text-5xl font-light">{c.heading}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(c.items || []).map((item: any, i: number) => (
            <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }}
              className="relative h-80 overflow-hidden group cursor-pointer">
              <img src={item.imageUrl || '/placeholder.svg'} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-primary/50 group-hover:bg-primary/30 transition-colors" />
              <div className="absolute bottom-6 left-6">
                <h3 className="font-serif text-xl text-primary-foreground">{item.title}</h3>
                {item.description && <p className="text-primary-foreground/60 text-sm mt-1">{item.description}</p>}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
